import { _x } from '@wordpress/i18n'
import { Tooltip } from 'bootstrap'
import type { Hit } from 'instantsearch.js'
import { useCallback, useEffect, useRef } from 'react'
import type { SwiperContainer } from 'swiper/element/bundle'
import { REM } from '../../../../assets/constants/fontsize'
import { useTooltip } from '../../../../assets/frontend/js/hooks/tooltip'
import { externalLinkProps } from '../../../../assets/frontend/js/utils/externalUrl'
import { l, t } from '../../../../assets/utils/algolia/algoliaLocalization'
import { commaList, getTimeString } from '../../../../assets/utils/format'
import useReplaceImgixUrl from '../../../../assets/utils/useReplaceImgixUrl'
import { useServicesUi } from '../context'
import { useConfigContext } from './ConfigContext'

type Speaker = {
  firstName: Record<string, unknown>
  fullName: Record<string, unknown>
  lastName: Record<string, unknown>
  image?: Record<
    string,
    {
      imageUrl?: string
      imageAltText?: string
    }
  >
}

function addImageTooltips(swiper: SwiperContainer) {
  const elements = swiper.querySelectorAll('.image')
  for (const element of elements) {
    new Tooltip(element as HTMLElement, {
      title: element.getAttribute('alt') ?? '',
      customClass: 'services-list-card-speaker-tooltip'
    })
  }
}

function removeImageTooltips(swiper: SwiperContainer) {
  const elements = swiper.querySelectorAll('.image')
  for (const element of elements) {
    Tooltip.getInstance(element as HTMLElement)?.dispose()
  }
}

function getSpeakerImages(speakers: Speaker[]) {
  const output: { url: string; alt?: string }[] = []
  for (const speaker of speakers) {
    const urlLocale = l(speaker.image, { fallback: 'any' })
    const langLocale = l(speaker.image, { fallback: 'lang' })
    const url = speaker.image?.[urlLocale]?.imageUrl
    const alt = speaker.image?.[langLocale]?.imageAltText
    if (url) {
      output.push({ url, alt })
    }
  }
  return output
}

function SpeakersSlider(props: { speakers: Speaker[] }) {
  const { speakers } = props

  if (!speakers) {
    return null
  }

  const speakerImages = getSpeakerImages(speakers)

  if (speakerImages.length === 0) {
    return null
  }

  const swiperRef = useRef<SwiperContainer>(null)

  useEffect(() => {
    const swiper = swiperRef.current
    if (swiper) {
      Object.assign(swiper, {
        navigation: false,
        slidesPerView: 1,
        spaceBetween: 1 * REM,
        pagination: {
          enabled: true,
          clickable: true
        }
      })
      swiper.initialize()
      addImageTooltips(swiper)
    }

    return () => {
      if (swiper) {
        swiper.disconnectedCallback()
        removeImageTooltips(swiper)
      }
    }
  }, [])

  const { imgixSrcUrl, imgixTargetUrl, siteUrl } = useConfigContext()

  const replaceImgixUrl = useReplaceImgixUrl(imgixSrcUrl, imgixTargetUrl)

  return (
    <swiper-container
      ref={swiperRef}
      init="false"
      /* @ts-ignore */
      class="services-list-card-speaker-slider"
    >
      {speakerImages.map(({ url, alt }, index: number) => {
        return url ? (
          // biome-ignore lint/suspicious/noArrayIndexKey:
          <swiper-slide key={index}>
            <img
              src={replaceImgixUrl(url, {
                ratio: '1',
                width: 150
              })}
              alt={alt}
              className="image"
            />
          </swiper-slide>
        ) : null
      })}
    </swiper-container>
  )
}

function ServiceListCard(props: {
  hit: Hit
}) {
  const elementRef = useRef<HTMLButtonElement>(null)
  const infoRef = useRef<HTMLElement>(null)

  const { selectedHit, selectHit } = useServicesUi()

  const { hit } = props
  const date = new Date(hit.date)
  const topicTitle = t(hit.topic)
  const speakerNames = commaList(
    (hit.speakers ?? [])
      .filter(Boolean)
      .map((s: Record<string, Speaker>) => t(s.fullName, { fallback: 'any' }))
  )
  const infoText = t(hit.infos)

  const isActive = selectedHit === hit.objectID

  useEffect(() => {
    if (isActive) {
      elementRef.current?.focus()
    }
  }, [isActive])

  useTooltip(
    infoRef,
    {
      title: infoText,
      customClass: 'services-series-tooltip',
      placement: 'left'
    },
    [infoText]
  )

  const handleFocus = useCallback(() => {
    selectHit(hit.objectID)
  }, [selectHit, hit.objectID])

  const { siteUrl } = useConfigContext()

  return (
    <button
      type="button"
      ref={elementRef}
      className={`card services-list-card w-100 ${isActive ? 'is-active' : ''}`}
      data-hit-id={hit.objectID}
      aria-selected={isActive}
      onFocus={handleFocus}
    >
      <div className="card-body">
        <div className="w-100 h-100 overflow-hidden">
          <div className="services-list-card-layout">
            <div className="time">
              <time className="services-time-badge" dateTime={hit.date}>
                {getTimeString(date, _x('h:i a', 'Celebration Time', 'icf'))}
              </time>
            </div>
            <div className="content">
              <p className="content-heading">{t(hit.title)}</p>
              {!!topicTitle && (
                // biome-ignore lint/a11y/useValidAriaProps: false positive
                <p
                  className="services-list-card-info-group mb-0"
                  aria-description={_x(
                    'Topic of the celebration',
                    'Celebration Topic Label',
                    'icf'
                  )}
                >
                  <i className="icon icon--fixed-width fa-book" />
                  {topicTitle}
                </p>
              )}
              {!!speakerNames && (
                // biome-ignore lint/a11y/useValidAriaProps: false positive
                <p
                  className="services-list-card-info-group"
                  aria-description={_x(
                    'Speakers of the celebration',
                    'Celebration Speaker Label',
                    'icf'
                  )}
                >
                  <i className="icon icon--fixed-width fa-user" />
                  {speakerNames}
                </p>
              )}
            </div>
            <div className="speaker">
              <SpeakersSlider speakers={hit.speakers} />
            </div>
            <div className="meta">
              <div className="services-list-card-meta">
                <div className="tags">
                  {hit.tags
                    ?.filter(Boolean)
                    .map((tag: { title?: string }, index: number) => (
                      <span key={`tag-${tag.title}-${index}`} className="tag">
                        {t(tag.title)}
                      </span>
                    ))}
                  {hit.languages.map(
                    (language: { name: string }, index: number) =>
                      language?.name ? (
                        <span
                          key={`tag-${language.name}-${index}`}
                          className="tag text-uppercase"
                        >
                          {language.name}
                        </span>
                      ) : null
                  )}
                </div>
                {!!infoText && (
                  <i className="icon fa-info-circle" ref={infoRef} />
                )}
              </div>
            </div>
            <div className="cta">
              {!!hit.vodUrl && (
                <a href={hit.vodUrl as string} {...externalLinkProps(hit.vodUrl as string, siteUrl)} className="btn btn-sm btn-primary">
                  <span>
                    {_x('Watch online', 'Celebration CTA Button', 'icf')}
                  </span>
                  {/* biome-ignore lint/a11y/noAriaHiddenOnFocusable: <explanation> */}
                  <i className="icon fa-arrow-right" aria-hidden="true" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </button>
  )
}

export default ServiceListCard
